import { SEOHandle } from "@nasa-gcn/remix-seo";
import {
  ActionFunctionArgs,
  json,
  LoaderFunctionArgs,
  MetaFunction,
  redirect,
} from "@remix-run/node";
import { useFetcher, useLoaderData, useSearchParams } from "@remix-run/react";
import { useEffect, useState } from "react";
import { parseFormData } from "remix-hook-form";

import SectionVirtualSearchBar from "~/components/sections/SectionVirtualSearchBar";
import SectionVirtualTours from "~/components/sections/SectionVirtualTours";
import { IVirtualTour } from "~/entities/virtualTour";
import useFirstItemsOfGroups from "~/hooks/use-first-item-of-groups";
import { getVirtualTours } from "~/services/virtualTourService";
import { IRouteLoaderData } from "~/types/routeLoaderData";
import { LIMIT, PAGE } from "~/utilities/constants/common";
import { EApiParam } from "~/utilities/enums/ApiParam";
import { getMetadata } from "~/utilities/helpers/metadata";
import { extractDomainFromRequest } from "~/utilities/helpers/requestHelper";

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  const { siteUrl } = { ...data };

  const rootMatch = matches?.find((match) => match?.id === "root");

  if (!rootMatch) {
    return [];
  }

  const { metadata } = rootMatch.data as IRouteLoaderData;

  return getMetadata({
    title: metadata?.title,
    description: metadata?.description,
    siteUrl,
    image: metadata?.ogImage,
    twitterCreator: metadata?.twitterCreator,
    twitterSite: metadata?.twitterSite,
    additionalMeta: [
      { name: "og:type", content: "website" },
      {
        name: "og:site_name",
        content: metadata?.title,
      },
    ],
  });
};

export const handle: SEOHandle = {
  getSitemapEntries: async () => {
    return [{ route: `/virtual-tours`, priority: 0.7 }];
  },
};

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request?.url || "");
  const domain = extractDomainFromRequest(request);
  const siteUrl = url.protocol + "//" + url.host + url.pathname;

  const page = url.searchParams.get(EApiParam.PAGE) || PAGE;
  const limit = url.searchParams.get(EApiParam.LIMIT) || LIMIT;
  const keyword = url.searchParams.get(EApiParam.KEYWORD) || "";

  const virtualToursResult = await getVirtualTours(domain, { keyword });
  return json({ siteUrl, virtualToursResult });
};

export async function action({ request }: ActionFunctionArgs) {
  const formData = await parseFormData<Record<string, string>>(request, false);
  const url = new URL(request?.url || "");
  Object.keys(formData)?.forEach((key) => {
    if (!!formData[key] && formData[key] !== "null") {
      return url.searchParams.set(key, formData[key]);
    }
    return url.searchParams.delete(key);
  });

  return redirect(url.toString());
}

export default function VirtualTours() {
  const { virtualToursResult } = useLoaderData<typeof loader>();
  const fetcher = useFetcher<typeof loader>();
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get(EApiParam.KEYWORD) || "";
  const [virtualTours, setVirtualTours] = useState<IVirtualTour[]>([]);
  const { firstItemsOfGroups, totalGroups, totalItems } = useFirstItemsOfGroups(
    fetcher?.data?.virtualToursResult?.items ?? virtualToursResult?.items
  );

  useEffect(() => {
    if (!fetcher.data || fetcher.state === "loading") {
      return;
    }

    if (fetcher.data) {
      const newItems = (fetcher?.data?.virtualToursResult?.items ||
        []) as unknown as IVirtualTour[];
      setVirtualTours((prevAssets) => [...prevAssets, ...newItems]);
    }
  }, [fetcher.data]);

  useEffect(() => {
    setVirtualTours((firstItemsOfGroups || []) as unknown as IVirtualTour[]);
  }, [keyword, firstItemsOfGroups]);

  // const onLoadMore = () => {
  //   if (
  //     fetcher.state === "loading" ||
  //     virtualTours?.length === virtualToursResult?.total
  //   )
  //     return;
  //   const page = fetcher?.data?.virtualToursResult?.currentPage
  //     ? fetcher?.data?.virtualToursResult?.currentPage + 1
  //     : virtualToursResult?.currentPage + 1;
  //   const limit = fetcher?.data?.virtualToursResult?.limit
  //     ? fetcher?.data?.virtualToursResult?.limit
  //     : virtualToursResult?.limit;
  //   const query = `?${EApiParam.PAGE}=${page}&${EApiParam.LIMIT}=${limit}&${EApiParam.KEYWORD}=${keyword}`;
  //   fetcher.load(query);
  // };

  return (
    <main>
      <SectionVirtualSearchBar />
      <SectionVirtualTours
        virtualTours={virtualTours}
        total={totalItems || 0}
        totalGroups={totalGroups || 0}
        onLoadMore={() => {}}
        isLoading={fetcher.state === "loading"}
        variant="virtual"
      />
    </main>
  );
}
